import { useCallback } from 'react'

import { useAiChat as useF0AiChat } from '@factorialco/f0-react'

import { useClarifyingQuestion } from 'modules/ai/chat/providers/ClarifyingQuestionProvider'
import { useFactorialChatRuntime } from 'modules/ai/chat/providers/FactorialChatRuntimeProvider'

export function useAiChat() {
  const ui = useF0AiChat()
  const rt = useFactorialChatRuntime()
  const clarifying = useClarifyingQuestion()

  // Optional-chain the setters so consumers that mock `useAiChat` with
  // only the runtime methods (clear/sendMessage/…) don't crash here.
  const resetForNewThread = useCallback(() => {
    ui.setPendingContext?.(null)
    ui.setPendingQuote?.(null)
    if (ui.visualizationMode === 'canvas') {
      ui.closeCanvas?.()
    }
  }, [ui])

  const clear = useCallback(() => {
    rt.clear()
    resetForNewThread()
  }, [rt, resetForNewThread])

  const loadThread = useCallback(
    (threadId: string, title: string) => {
      resetForNewThread()
      rt.loadThread(threadId, title)
    },
    [rt, resetForNewThread]
  )

  const clearAndAppend = useCallback(
    (msgs: Parameters<typeof rt.clearAndAppend>[0]) => {
      resetForNewThread()
      rt.clearAndAppend(msgs)
    },
    [rt, resetForNewThread]
  )

  return {
    ...rt,
    ...ui,
    ...clarifying,
    clear,
    loadThread,
    clearAndAppend,
  }
}
